import { type JSX } from 'react';

import { ARTEN, ARTNAME, type Ressourcenart } from '../api/ressourcen';
import type { Bedarfsdeckung, Belegungsressource } from '../api/tagesplanung';
import { Terminkopf, type Terminspalte } from './Tagesraster';

/**
 * Der Tagesplan auf dem Telefon: je Termin eine Karte (Auftrag CRM-106, ÄA-63).
 *
 * ============================================================================
 * WARUM NICHT DIE ZEITACHSE
 * ============================================================================
 *
 * Auf der Zeitachse bestimmt die Dauer die Breite (siehe `Tagesraster.tsx`).
 * Bahn und Achse brauchen mindestens 22rem; bei 390 px bleibt für eine
 * Station von 45 Minuten ein Streifen, der weder lesbar noch mit dem Finger
 * zu treffen ist. Das Raster braucht je Termin eine Spalte — ab drei
 * Terminen rollt es quer.
 *
 * Die Karte stellt dieselbe Sache als Liste dar: Termin oben, darunter die
 * Stationen in ihrer Reihenfolge mit Uhrzeit, Ort und Trainer als Text.
 * TAG-03 verlangt ein Raster aus Blöcken und Ressourcen und keine bestimmte
 * Form (ÄA-09); eine dritte Darstellung fasst keine Anforderung an.
 *
 * NUR LESEN. Anfassen, Verschieben und Setzen bleiben Desktop und Tablet
 * vorbehalten — die Karte bietet keinen Knopf an. Die freien Stellen stehen
 * trotzdem da: Ein Plan, der nur das Belegte zeigt, verschweigt, wo noch
 * Platz ist (TAG-03).
 */
export function Tageskarten({
  spalten,
  beginnVon,
  tagesende,
  ressourcenDer,
  bedarfDer,
}: {
  readonly spalten: readonly Terminspalte[];
  /** Die Uhrzeit eines Blocks; der Block nach dem letzten endet mit dem Tag. */
  readonly beginnVon: (block: number) => string | undefined;
  readonly tagesende: string;
  readonly ressourcenDer: (belegung: string) => readonly Belegungsressource[];
  readonly bedarfDer: (belegung: string) => readonly Bedarfsdeckung[];
}): JSX.Element {
  const zeitVon = (block: number): string => beginnVon(block) ?? tagesende;

  return (
    <div className="tageskarten">
      {spalten.map((t) => (
        <section
          key={t.termin}
          className={t.probeAus === true ? 'tageskarte tageskarte--probe-aus' : 'tageskarte'}
          aria-label={`${t.produkt}, ab ${t.ab}`}
        >
          {/*
            Derselbe Kopf wie über Rasterspalte und Zeitachsenzeile — eine
            Stelle, drei Verwender. Ohne `beiTermin`: Der Name bleibt Text,
            das Terminfenster mit Absage und Vereinbarung ist ein Eingriff.
          */}
          <div className="tageskarte__kopf">
            <Terminkopf spalte={t} />
          </div>
          <ol className="tageskarte__folge" aria-label={`Ablauf ${t.produkt}, ab ${t.ab}`}>
            {t.abschnitte.map((a) =>
              a.art === 'belegt' ? (
                <Station
                  key={a.b.belegung}
                  zeit={`${a.b.vonZeit}–${a.b.bisZeit}`}
                  name={a.b.bausteinname ?? a.b.aktivitaetsart}
                  abweichend={a.b.abweichend}
                  ressourcen={ressourcenDer(a.b.belegung)}
                  luecken={bedarfDer(a.b.belegung).filter((d) => d.zugeteilt < d.verlangt)}
                />
              ) : (
                <li key={`frei-${String(a.von)}`} className="tageskarte__zeile tageskarte__frei">
                  <span className="tageskarte__zeit">
                    {zeitVon(a.von)}–{zeitVon(a.von + a.laenge)}
                  </span>{' '}
                  <span className="hilfe">frei</span>
                </li>
              ),
            )}
          </ol>
        </section>
      ))}
    </div>
  );
}

/** Die Beschriftung einer Ressourcenart — Unbekanntes steht, wie es kommt. */
function artname(art: string): string {
  return (ARTNAME as Readonly<Record<string, string>>)[art] ?? art;
}

/** Die Reihenfolge der Arten wie in der Ressourcenverwaltung; Unbekanntes zuletzt. */
function artrang(art: string): number {
  const stelle = (ARTEN as readonly string[]).indexOf(art as Ressourcenart);
  return stelle < 0 ? ARTEN.length : stelle;
}

/**
 * Eine Station: Uhrzeit, Baustein und was ihr zugeteilt ist (TAG-03, DAT-14).
 *
 * TAG-03 verlangt je Zelle Aktivität, Trainer und Betriebsmittel. Die
 * Ressourcen stehen nach Art gebündelt — „Raum: Schulungsraum 1" — und nicht
 * als eine Aufzählung, in der man den Raum zwischen drei Fahrzeugen sucht.
 */
function Station({
  zeit,
  name,
  abweichend,
  ressourcen,
  luecken,
}: {
  readonly zeit: string;
  readonly name: string;
  readonly abweichend: boolean;
  readonly ressourcen: readonly Belegungsressource[];
  readonly luecken: readonly Bedarfsdeckung[];
}): JSX.Element {
  const jeArt = new Map<string, string[]>();
  for (const r of ressourcen) jeArt.set(r.art, [...(jeArt.get(r.art) ?? []), r.bezeichnung]);
  const arten = [...jeArt.keys()].sort((x, y) => artrang(x) - artrang(y));

  return (
    <li className="tageskarte__zeile tageskarte__station">
      <span className="tageskarte__zeit">{zeit}</span> <strong>{name}</strong>
      {abweichend && (
        <>
          {' '}
          <span className="marke">abweichend</span>
        </>
      )}
      {arten.map((art) => (
        <span key={art} className="tageskarte__ressource">
          {artname(art)}: {(jeArt.get(art) ?? []).join(', ')}
        </span>
      ))}
      {/* TAG-15, RES-24: Was fehlt, ist ein Konflikt, wenn es zwingend ist (ÄA-41: Rost). */}
      {luecken.map((d) => (
        <span key={d.bedarf} className="tageskarte__ressource">
          <span className={d.zwingend ? 'marke marke--warn' : 'marke'}>
            {d.bedarfsname} fehlt ({d.zugeteilt} von {d.verlangt})
          </span>
        </span>
      ))}
    </li>
  );
}
